import React, { forwardRef, useContext, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grow,
  IconButton,
  Typography,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Close } from '@mui/icons-material'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { IntakeContext } from 'context/IntakeContext'
import { toast } from 'react-toastify'

import { useQuilttIntegration } from 'hooks/useQuilttIntegration'
import { QuilttButton } from '@quiltt/react'
import { QuilttLogo } from 'components/QuilttLogo'

import styled from 'styled-components'

const StyledDiv = styled.div`
  button {
    border: none;
    cursor: pointer;
    padding: 6px 18px;
    font-size: 16px;
    line-height: 1.5;
    color: #8f62f8;
    background-color: transparent;
    font-weight: 500;
    text-transform: capitalize;
    svg {
      font-size: 1.5rem !important;
      margin-right: 4px;
      margin-bottom: -2px;
    }
  }
  button:hover, button:focus, button:active, button:focus:not(:hover): {
    transform: 'none';
    boxshadow: 'none';
  }
`

const StyledSpan = styled.span`
  width: auto;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #8f62f8;
  color: #8f62f8;
  background-color: #f5ebfe;
  &:hover {
    background-color: #ddcffc;
    cursor: pointer;
  }
`

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

export default function FieldModal({
  onClose,
  title = `Let's update your First Name!`,
  userField = 'firstName',
  inputType = 'text',
  fieldLabel = 'First Name',
  buttonText = 'Update',
  getAccounts,
  quilttConnect,
}) {
  const { reloadUser, intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const [localValue, setLocalValue] = useState(intakeState[userField] || null)

  const onSubmit = async () => {
    const updatedState = updateIntakeStateAtKey({ [userField]: localValue })
    await updateUser(updatedState)
    toast.info(`You have successfully updated your ${fieldLabel}`, {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
    onClose()
  }

  const { refreshQuiltt, handleExitSuccess } = useQuilttIntegration(() => {
    console.log('Accounts updated!')
    getAccounts()
  }, onClose)

  return (
    <Dialog
      open
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth={'sm'}
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogContent sx={{ overflow: 'visible' }}>
        <motion.div
          key="scheduler"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <Box
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              top: -64,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 0,
                right: -16,
                color: '#fff',
                zIndex: 1,
                backgroundColor: '#000',
                '&:hover': {
                  backgroundColor: '#000',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box p={3}>
            <Box>
              <Typography variant="headerTitle" sx={{ color: '#262627' }}>
                {title}
              </Typography>
            </Box>
            <Box mt={3}>
              {renderMuiFormField({
                id: userField,
                label: fieldLabel,
                placeholder: '',
                inputType: inputType,
                value: localValue,
                onChange: setLocalValue,
              })}
            </Box>
            {quilttConnect && (
              <Box pt={2}>
                <StyledDiv>
                  <StyledSpan>
                    <QuilttButton
                      connectorId="5ugr9q35ag"
                      institution="Thrift"
                      onExitSuccess={handleExitSuccess}
                      className="quiltt-Container-Class"
                    >
                      Connect TSP with <QuilttLogo />
                    </QuilttButton>
                  </StyledSpan>
                </StyledDiv>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                rel="noreferrer"
                size="large"
                variant="contained"
                onClick={onClose}
                sx={{
                  padding: '8px 24px',
                  backgroundColor: '#FFF',
                  border: '2px solid #262627',
                  color: '#262627',
                  boxShadow: 'none',
                  borderRadius: '100px',
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: 1.5,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#f1f1f1',
                    borderColor: '#262627',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    borderColor: 'rgba(0,0,0,0)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                rel="noreferrer"
                size="large"
                variant="contained"
                onClick={onSubmit}
                sx={{
                  padding: '8px 24px',
                  backgroundColor: '#2370ED',
                  border: `2px solid #2370ED`,
                  color: '#fff',
                  boxShadow: 'none',
                  borderRadius: '100px',
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: 1.5,
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#1c59bd',
                    borderColor: '#1c59bd',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    borderColor: 'rgba(0,0,0,0)',
                  },
                }}
              >
                {buttonText}
              </Button>
            </Box>
          </Box>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}
